import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/platform-web3/auth.service';
import { AuthInfo, UserState } from 'src/app/platform-web3/user.state';
import { UserRoleState } from 'src/app/platform-web3/user-role.state';
import { StudentRecord } from 'src/app/data';
import { AuthQRCodeComponent } from '../auth-qrcode/auth-qrcode.component';
import { CopyrightContentComponent } from '../header-bar/terms/copyright-content.component';
import { PrivacyContentComponent } from '../header-bar/terms/privacy-content.component';
import { ServiceContentComponent } from '../header-bar/terms/service-content.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

@Component({
  selector: 'app-account-setting',
  templateUrl: './account-setting.component.html',
  styleUrls: ['./account-setting.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingComponent implements OnInit, OnDestroy {

  unSubscribe$ = new Subject();
  authInfo: AuthInfo = {} as AuthInfo;

  advanceUser = false;
  powerBackendUser = false;

  // 親子綁定相關屬性
  hasStudentRole = false;
  studentRoles: StudentRecord[] = [];
  selectedStudentRole: StudentRecord | null = null;
  parentQRCodeURL = '';
  parentCode = '';
  isLoading = false;
  errorMessage = '';

  @ViewChild('tplParentBinding', { static: false }) tplParentBinding!: TemplateRef<any>;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private auth: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(I18NEXT_SERVICE) private i18NextSrv: ITranslationService
  ) { }

  ngOnInit(): void {
    this.store.select(UserState.authInfo).pipe(
      takeUntil(this.unSubscribe$)
    ).subscribe(u => {
      this.authInfo = (u ?? {}) as AuthInfo

      const account = this?.authInfo?.account ?? '';
      this.powerBackendUser = [
        'yaoming.huang@ischool.com.tw'
      ].indexOf(account) >= 0;
      this.changeDetectorRef.detectChanges();
    });

    // 監聽角色變化
    this.store.select(UserRoleState.getRoles).pipe(
      takeUntil(this.unSubscribe$)
    ).subscribe(roles => {
      // 篩選出所有學生身分
      this.studentRoles = roles.filter(role => role.roleName === 'student') as StudentRecord[];
      this.hasStudentRole = this.studentRoles.length > 0;

      // 設定預設選擇的學生身分
      if (this.studentRoles.length > 0) {
        this.selectedStudentRole = this.studentRoles[0];
      }

      this.changeDetectorRef.detectChanges();
    });

    this.i18NextSrv.events.languageChanged.subscribe(lang => {
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    this.advanceUser = event.shiftKey && event.altKey;
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    // this.advanceUser = event.shiftKey;
  }

  openModal(content: any, config: MatDialogConfig) {
    this.dialog.open(content, config);
  }

  /** QR Code */
  async openQRModal() {
    this.openModal(AuthQRCodeComponent, {
      width: '280px',
      autoFocus: false
    });
  }

  /** 登出帳號 */
  logoutAccount(list: string[]) {
    this.auth.logout(list);
  }

  /** 登出 */
  logout() {
    this.logoutAccount([]);
  }

  openServiceContent() {
    window.open('https://www.ischool.com.tw/terms.html#service', '_blank', 'noopener,noreferrer');
  }

  openPrivacyContent() {
    window.open('https://www.ischool.com.tw/terms.html#privacy', '_blank', 'noopener,noreferrer');
  }

  openCopyRightContent() {
    window.open('https://www.ischool.com.tw/terms.html#copyright', '_blank', 'noopener,noreferrer');
  }

  btnDomainManager() {
    const target = `https://auth.ischool.com.tw/default/domainmanage.php`;
    window.open(`/auth/linkout?redirect_url=${encodeURIComponent(target)}`);
  }

  /**
   * 開啟親子綁定 Modal
   */
  openParentBindingModal() {
    if (this.studentRoles.length === 0) {
      return;
    }

    // 重置狀態
    this.parentQRCodeURL = '';
    this.parentCode = '';
    this.isLoading = false;
    this.errorMessage = '';

    // 開啟 Modal
    this.openModal(this.tplParentBinding, { width: '400px', autoFocus: false });

    // 載入第一個學生的親子綁定資料
    this.loadParentBindingData();
  }

  /**
   * 學校切換事件
   */
  onSchoolChange() {
    this.loadParentBindingData();
  }

  /**
   * 載入親子綁定資料
   */
  private async loadParentBindingData() {
    if (!this.selectedStudentRole) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.parentQRCodeURL = '';
    this.parentCode = '';

    try {
      const response = await fetch(`/service/roleClaim/showParentQRCode?dsns=${this.selectedStudentRole.dsns}&studentID=${this.selectedStudentRole.id}`);

      const data = await response.json();

      if (response.ok && data?.success && data?.parentCode) {
        // 成功取得家長代碼
        this.parentCode = data.parentCode;
        // 生成 QRCode URL
        this.parentQRCodeURL = `https://devapi.1campus.net/api/code/qrcode/img?chld=M&chs=246x246&cht=qr&chl=${data.parentCode}@${this.selectedStudentRole.dsns}&choe=UTF-8`;
      } else {
        // 家長代碼不存在或其他錯誤情況
        this.parentCode = '****';
        this.parentQRCodeURL = '';

        // 根據錯誤碼決定顯示的錯誤訊息
        const errorCode = data?.code;

        switch (errorCode) {
          case '501':
            this.errorMessage = '尚未設定家長代碼，請與管理人員聯絡';
            break;
          case '403':
            this.errorMessage = '身分驗證失敗，您無權查詢此學生的家長代碼';
            break;
          case '401':
            this.errorMessage = '登入已過期，請重新登入';
            break;
          default:
            if (!response.ok) {
              this.errorMessage = '尚未設定家長代碼，請與管理人員聯絡';
            } else {
              this.errorMessage = '尚未設定家長代碼，請與管理人員聯絡';
            }
        }

        // diagnostic 僅用於除錯
        console.log('API 錯誤詳情:', data?.diagnostic);
      }
    } catch (error) {
      console.error('載入親子綁定資料失敗:', error);
      this.parentCode = '****';
      this.parentQRCodeURL = '';
      this.errorMessage = '載入失敗，請稍後再試';
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
